import React, { useState, useEffect } from 'react';
import usePageTitle from '../hooks/usePageTitle';

import { getCostCentersAll, createCostCenter, updateCostCenter, deleteCostCenter, getUsers } from '../utils/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BanknotesIcon,
  CheckIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';

const CostCenters = () => {
  usePageTitle('Cost Centers');

  const [costCenters, setCostCenters] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editingCostCenter, setEditingCostCenter] = useState(null);
  const [viewingCostCenter, setViewingCostCenter] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    description: '',
    parent_id: '',
    owner_id: '',
    is_vat: false,
    is_active: true,
  });

  useEffect(() => {
    loadCostCenters();
    loadUsers();
  }, []);

  const loadCostCenters = async () => {
    try {
      const response = await getCostCentersAll();
      setCostCenters(response.data);
    } catch (error) {
      toast.error('Error loading cost centers');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await getUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const openModal = (costCenter = null) => {
    if (costCenter) {
      setEditingCostCenter(costCenter);
      setFormData({
        name: costCenter.name || '',
        code: costCenter.code || '',
        description: costCenter.description || '',
        parent_id: costCenter.parent_id || '',
        owner_id: costCenter.owner_id || '',
        is_vat: Boolean(costCenter.is_vat),
        is_active: Boolean(costCenter.is_active),
      });
    } else {
      setEditingCostCenter(null);
      setFormData({
        name: '',
        code: '',
        description: '',
        parent_id: '',
        owner_id: '',
        is_vat: false,
        is_active: true,
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingCostCenter(null);
  };

  const openViewModal = (costCenter) => {
    setViewingCostCenter(costCenter);
    setViewModalOpen(true);
  };

  const closeViewModal = () => {
    setViewModalOpen(false);
    setViewingCostCenter(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.code) {
      toast.error('Cost center name and code are required');
      return;
    }

    if (formData.description && formData.description.length > 500) {
      toast.error('Description must be 500 characters or less');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        description: formData.description || null,
        parent_id: formData.parent_id || null,
        owner_id: formData.owner_id || null,
      };

      if (editingCostCenter) {
        await updateCostCenter(editingCostCenter.id, submitData);
        toast.success('Cost center updated successfully');
      } else {
        await createCostCenter(submitData);
        toast.success('Cost center created successfully');
      }
      closeModal();
      loadCostCenters();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error saving cost center');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (costCenter) => {
    if (!window.confirm(`Are you sure you want to delete "${costCenter.name}" (${costCenter.code})?`)) {
      return;
    }

    try {
      await deleteCostCenter(costCenter.id);
      toast.success('Cost center deleted successfully');
      loadCostCenters();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting cost center');
    }
  };

  // Get available parent cost centers (excluding the current one when editing)
  const getAvailableParents = () => {
    if (editingCostCenter) {
      return costCenters.filter(cc => cc.id !== editingCostCenter.id);
    }
    return costCenters;
  };

  // Filter cost centers based on search query
  const filteredCostCenters = costCenters.filter((cc) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      cc.code?.toLowerCase().includes(searchLower) ||
      cc.name?.toLowerCase().includes(searchLower) ||
      cc.parent_name?.toLowerCase().includes(searchLower) ||
      cc.owner_name?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return <div className="loading-overlay"><div className="spinner" /><span>Loading cost centers...</span></div>;
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Cost Centers</h1>
          <p className="page-subtitle">Manage cost centers for expense tracking</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <PlusIcon style={{ width: 18, height: 18 }} />
          Add Cost Center
        </button>
      </div>

      <div className="card">
        {costCenters.length > 0 && (
          <div className="search-bar">
            <div className="search-input-wrapper">
              <MagnifyingGlassIcon className="search-icon" style={{ width: 18, height: 18 }} />
              <input
                type="text"
                className="form-input search-input"
                placeholder="Search by code, name, parent, or owner..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        )}

        {filteredCostCenters.length > 0 ? (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Parent</th>
                  <th>Owner</th>
                  <th style={{ textAlign: 'center' }}>VAT</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th style={{ width: 100 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCostCenters.map((costCenter) => (
                  <tr key={costCenter.id}>
                    <td className="font-mono"><strong>{costCenter.code}</strong></td>
                    <td>{costCenter.name}</td>
                    <td>{costCenter.parent_name || '-'}</td>
                    <td>{costCenter.owner_name || '-'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {costCenter.is_vat ? (
                        <CheckIcon style={{ width: 18, height: 18, color: 'var(--color-accent-success)' }} />
                      ) : (
                        <XMarkIcon style={{ width: 18, height: 18, color: 'var(--color-text-tertiary)' }} />
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {costCenter.is_active ? (
                        <span className="badge badge-approved">Active</span>
                      ) : (
                        <span className="badge badge-draft">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => openViewModal(costCenter)}
                          title="View Details"
                        >
                          <EyeIcon style={{ width: 16, height: 16 }} />
                        </button>
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => openModal(costCenter)}
                          title="Edit"
                        >
                          <PencilIcon style={{ width: 16, height: 16 }} />
                        </button>
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => handleDelete(costCenter)}
                          title="Delete"
                          style={{ color: 'var(--color-accent-danger)' }}
                        >
                          <TrashIcon style={{ width: 16, height: 16 }} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : costCenters.length === 0 ? (
          <div className="empty-state">
            <BanknotesIcon className="empty-state-icon" />
            <p className="empty-state-title">No cost centers found</p>
            <p>Add your first cost center to get started</p>
            <button className="btn btn-primary" onClick={() => openModal()} style={{ marginTop: '1rem' }}>
              <PlusIcon style={{ width: 18, height: 18 }} />
              Add Cost Center
            </button>
          </div>
        ) : (
          <div className="empty-state">
            <MagnifyingGlassIcon className="empty-state-icon" />
            <p className="empty-state-title">No cost centers found</p>
            <p>No cost centers match your search criteria</p>
            <button className="btn btn-secondary" onClick={() => setSearch('')} style={{ marginTop: '1rem' }}>
              Clear Search
            </button>
          </div>
        )}
      </div>

      {/* Cost Center Form Modal */}
      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingCostCenter ? 'Edit Cost Center' : 'Add New Cost Center'}
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editingCostCenter ? 'Update Cost Center' : 'Create Cost Center'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Cost Center Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter cost center name"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Cost Center Code *</label>
              <input
                type="text"
                className="form-input"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="e.g., CC001, DEPT-IT"
                style={{ textTransform: 'uppercase' }}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Parent Cost Center</label>
              <select
                className="form-select"
                value={formData.parent_id}
                onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
              >
                <option value="">None (Top Level)</option>
                {getAvailableParents().map((cc) => (
                  <option key={cc.id} value={cc.id}>
                    {cc.code} - {cc.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Owner</label>
              <select
                className="form-select"
                value={formData.owner_id}
                onChange={(e) => setFormData({ ...formData, owner_id: e.target.value })}
              >
                <option value="">Select owner</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.full_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Enter cost center description (optional)"
              rows={4}
              maxLength={500}
            />
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', marginTop: 'var(--space-xs)' }}>
              {formData.description.length}/500 characters
            </div>
          </div>

          <div className="form-row">
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingBottom: '0.5rem' }}>
              <label className="form-checkbox">
                <input
                  type="checkbox"
                  checked={formData.is_vat}
                  onChange={(e) => setFormData({ ...formData, is_vat: e.target.checked })}
                />
                <span>VAT Cost Center</span>
              </label>
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'center', paddingBottom: '0.5rem' }}>
              <label className="form-checkbox">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <span>Active</span>
              </label>
            </div>
          </div>
        </form>
      </Modal>

      {/* View Cost Center Modal */}
      <Modal
        isOpen={viewModalOpen}
        onClose={closeViewModal}
        title="Cost Center Details"
        size="lg"
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeViewModal}>Close</button>
            <button className="btn btn-primary" onClick={() => { closeViewModal(); openModal(viewingCostCenter); }}>
              Edit Cost Center
            </button>
          </>
        }
      >
        {viewingCostCenter && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Cost Center Code</label>
                <div className="font-mono" style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  <strong>{viewingCostCenter.code}</strong>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Cost Center Name</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  {viewingCostCenter.name}
                </div>
              </div>
            </div>

            {viewingCostCenter.description && (
              <div className="form-group">
                <label className="form-label">Description</label>
                <div style={{ padding: 'var(--space-md)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)', whiteSpace: 'pre-wrap' }}>
                  {viewingCostCenter.description}
                </div>
              </div>
            )}

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Parent Cost Center</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  {viewingCostCenter.parent_name || '-'}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Owner</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  {viewingCostCenter.owner_name || '-'}
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">VAT Cost Center</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                  {viewingCostCenter.is_vat ? (
                    <>
                      <CheckIcon style={{ width: 18, height: 18, color: 'var(--color-accent-success)' }} />
                      Yes
                    </>
                  ) : (
                    <>
                      <XMarkIcon style={{ width: 18, height: 18, color: 'var(--color-text-tertiary)' }} />
                      No
                    </>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Status</label>
                <div style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)' }}>
                  {viewingCostCenter.is_active ? (
                    <span className="badge badge-approved">Active</span>
                  ) : (
                    <span className="badge badge-draft">Inactive</span>
                  )}
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Created At</label>
                <div className="font-mono" style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)', fontSize: '0.875rem' }}>
                  {new Date(viewingCostCenter.created_at).toLocaleString()}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Last Updated</label>
                <div className="font-mono" style={{ padding: 'var(--space-sm)', background: 'var(--color-bg-tertiary)', borderRadius: 'var(--radius-sm)', fontSize: '0.875rem' }}>
                  {viewingCostCenter.updated_at ? new Date(viewingCostCenter.updated_at).toLocaleString() : '-'}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CostCenters;
